import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";
import {
  transformAttribute,
  transformTranslatableTerm,
  transformBrand,
  transformCategoryNode,
} from "../helpers/productTaxonomyApiHelpers";
import {
  RawWPRestProductTagsResponse,
  RawWPRestBrand,
  RawWPRestBrandsResponse,
  RawWPRestAttributesResponse,
  RawWPRestAttribute,
  RawCategoryTreeResponse,
} from "../../../../types/index";

import type {
  GetCategoriesResult,
  TransformedWPTag,
  GetWPTagsResult,
  GetBrandsResult,
  GetAttributesResult,
  VehicleNode,
} from "../types/productTaxonomyApiTypes";

// Get the WordPress REST API base URL from theme variables or use default fallback
const WP_REST_API_BASE =
  window.commonVariables?.wpRestApiBase || "/wp-json/wp/v2";

const CUSTOM_REST_API_BASE =
  window.commonVariables?.customRestApiBase || "/wp-json/ecommerce-app/v1";

// Define the taxonomy API slice by injecting endpoints into the baseApi setup
export const taxonomyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getVehicles: builder.query<VehicleNode[], number[] | void>({
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}:${[...(queryArgs || [])].sort((a, b) => a - b).join(",")}`,
      query: (category) => {
        const ids = category || [];
        return {
          url: `${CUSTOM_REST_API_BASE}/vehicles/tree`,
          params: ids.length ? { category: ids.join(",") } : {},
        };
      },
      providesTags: [{ type: "Category", id: "VEHICLES" }],
    }),
    /**
     * Endpoint to fetch the unified product category tree.
     * Replaces standard WP REST and custom Mega Menu endpoints with a single rich hierarchy.
     * @returns Hierarchical array of transformed category nodes.
     */
    getCategories: builder.query<GetCategoriesResult, void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/categories/tree`,
        method: "GET",
      }),
      transformResponse: (
        response: RawCategoryTreeResponse,
      ): GetCategoriesResult => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getCategories expected an array:",
            response,
          );
          return [];
        }

        // Process the recursive tree using the helper
        return response.map(transformCategoryNode);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((cat) => ({
                type: "Category" as const,
                id: cat.id,
              })),
              { type: "Category" as const, id: "LIST" },
            ]
          : [{ type: "Category" as const, id: "LIST" }],
    }),

    /**
     * Endpoint to fetch product tags from WordPress REST API.
     * Provides a rich dataset including taxonomy data.
     * @returns Object containing an array of transformed WP tags.
     */
    getTags: builder.query<GetWPTagsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_tag`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductTagsResponse,
      ): GetWPTagsResult => {
        const languageInfo = selectLanguageInfo(store.getState());

        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getTags expected an array but received:",
            response,
          );
          return { tags: {} }; // Return a valid empty state
        }

        const tagsById = response.reduce(
          (acc, tag) => {
            const transformedTag = transformTranslatableTerm(tag, languageInfo);
            acc[tag.id] = transformedTag;
            return acc;
          },
          {} as Record<number, TransformedWPTag>,
        );

        // Return the tags indexed by ID
        return { tags: tagsById };
      },
      providesTags: (result) =>
        result?.tags
          ? [
              ...Object.keys(result.tags).map((id) => ({
                type: "Tag" as const,
                id,
              })),
              { type: "Tag" as const, id: "LIST" },
            ]
          : [{ type: "Tag" as const, id: "LIST" }],
    }),

    /**
     * Fetches product brands from the WordPress REST API.
     * Uses /wp/v2/product_brand endpoint.
     */
    getBrands: builder.query<GetBrandsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_brand`,
        params: { per_page: 100, orderby: "name", order: "asc" }, // Fetch up to 100 brands, order by name
      }),
      transformResponse: (
        response: RawWPRestBrandsResponse,
      ): GetBrandsResult => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getBrands expected an array but received:",
            response,
          );
          return { brands: {} }; // Return a valid empty state
        }
        const rawBrands: RawWPRestBrand[] = response;

        const brandsById = rawBrands.reduce(
          (acc, brand) => {
            acc[brand.id] = transformBrand(brand);
            return acc;
          },
          {} as GetBrandsResult["brands"],
        );

        return { brands: brandsById };
      },
      providesTags: (result) =>
        result?.brands
          ? [
              ...Object.keys(result.brands).map((id) => ({
                type: "Brand" as const,
                id,
              })),
              { type: "Brand" as const, id: "LIST" },
            ]
          : [{ type: "Brand" as const, id: "LIST" }],
    }),

    /**
     * Fetches enriched product attributes from our custom REST endpoint.
     */
    getAttributes: builder.query<GetAttributesResult, void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/product_attributes`,
      }),
      transformResponse: (
        response: RawWPRestAttributesResponse,
      ): GetAttributesResult => {
        const languageInfo = selectLanguageInfo(store.getState());

        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getAttributes expected an array but received:",
            response,
          );
          return { attributes: {} }; // Return a valid empty state
        }
        const rawAttributes: RawWPRestAttribute[] = response;

        const attributesById = rawAttributes.reduce(
          (acc, attribute) => {
            acc[attribute.id] = transformAttribute(attribute, languageInfo); // Pass dependencies
            return acc;
          },
          {} as GetAttributesResult["attributes"],
        );

        return { attributes: attributesById };
      },
      providesTags: (result) =>
        result?.attributes
          ? [
              ...Object.keys(result.attributes).map((id) => ({
                type: "Attribute" as const,
                id,
              })),
              { type: "Attribute" as const, id: "LIST" },
            ]
          : [{ type: "Attribute" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

// Export the auto-generated hooks from the taxonomyApi slice for use in components
export const {
  useGetVehiclesQuery,
  useGetCategoriesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
  useGetAttributesQuery,
} = taxonomyApi;

// Export the endpoint definitions themselves for potential use in SSR, testing, or manual dispatching
export const { getCategories, getTags, getBrands, getAttributes, getVehicles } =
  taxonomyApi.endpoints;
